// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: activity tracking for Team insights and the Assistant stage. `Activity.track(type, props)`
// queues one event; the queue is sent every 10 seconds, when it reaches 100 events, and when the
// page is hidden or closed (a keepalive request, so it survives unload). Offline or failed sends
// are kept and retried; the last unsent events survive a reload through localStorage.
// Only what happened is reported, never what was written: no message, mail, note or search text.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import type {
	ActivityArea,
	ActivityEvent,
	ActivityEventType,
	DeviceKind,
} from '@konvo/schema/src/domains/konvo/AssistantEngagement';
import {reaction} from 'mobx';

export type ActivityProps = Record<string, string | number | boolean | null>;

const logger = new Logger('Activity');

const FLUSH_MS = 10_000;
const BATCH = 100;
/** Kept while offline; the oldest go first beyond this. */
const MAX_QUEUE = 500;
const STORAGE_KEY = 'konvo:activity:queue';
const MAX_STRING = 200;

function cleanProps(props: ActivityProps): ActivityEvent['props'] {
	const out: ActivityEvent['props'] = {};
	for (const [key, value] of Object.entries(props)) {
		if (value === undefined) continue;
		out[key.slice(0, 40)] = typeof value === 'string' ? value.slice(0, MAX_STRING) : value;
	}
	return out;
}

export interface ActivitySession {
	token(): string | null;
	apiBase(): string | null;
	device(): DeviceKind;
	/** Shared browser/native UI visibility, supplied without importing the window store here. */
	visible?(): boolean;
}

export class ActivityStore {
	private queue: Array<ActivityEvent> = [];
	private timer: ReturnType<typeof setTimeout> | null = null;
	private sending = false;
	private listening = false;
	private pageHidden = false;
	private stopVisibility: (() => void) | null = null;
	private readonly beforeFlush = new Set<() => void>();
	/**
	 * The session token and API base, handed in by ActivityTracker so this module (imported by
	 * many stores) stays free of the app's runtime config and session.
	 */
	private session: ActivitySession | null = null;
	/** The area the person is in right now (kept by the area tracker); fills `action.done`'s area. */
	currentArea: ActivityArea | null = null;

	/** Called once by ActivityTracker: how to authenticate and where to send. */
	configure(session: ActivitySession): void {
		this.session = session;
	}

	track(type: ActivityEventType, props: ActivityProps = {}): void {
		// Logged out: nothing is recorded. Before configure() events wait in the queue.
		if (this.session && !this.session.token()) return;
		const clean = cleanProps(props);
		if (type === 'action.done' && typeof clean['area'] !== 'string') clean['area'] = this.currentArea ?? 'other';
		this.queue.push({type, at: new Date().toISOString(), device: this.device, props: clean});
		if (this.queue.length > MAX_QUEUE) this.queue.splice(0, this.queue.length - MAX_QUEUE);
		if (this.hidden) this.persist();
		else if (this.queue.length >= BATCH) {
			this.clearTimer();
			void this.flush();
		} else this.schedule();
	}

	/** Sends only when there are events; idle and hidden pages have no upload timer. */
	start(): void {
		if (this.listening || typeof window === 'undefined') return;
		this.listening = true;
		this.pageHidden = false;
		this.restore();
		document.addEventListener('visibilitychange', this.onVisibility);
		window.addEventListener('pagehide', this.onHide);
		window.addEventListener('pageshow', this.onShow);
		window.addEventListener('online', this.onOnline);
		window.addEventListener('offline', this.onOffline);
		if (this.session?.visible) {
			this.stopVisibility = reaction(
				() => this.session?.visible?.(),
				(visible) => {
					if (visible) this.onShow();
					else this.onHide();
				},
			);
		}
		this.schedule();
	}

	stop(): void {
		this.clearTimer();
		this.stopVisibility?.();
		this.stopVisibility = null;
		if (!this.listening) return;
		this.listening = false;
		document.removeEventListener('visibilitychange', this.onVisibility);
		window.removeEventListener('pagehide', this.onHide);
		window.removeEventListener('pageshow', this.onShow);
		window.removeEventListener('online', this.onOnline);
		window.removeEventListener('offline', this.onOffline);
	}

	/** Runs right before an unload flush, e.g. to close the open area visit. */
	onBeforeFlush(callback: () => void): () => void {
		this.beforeFlush.add(callback);
		return () => this.beforeFlush.delete(callback);
	}

	get device(): DeviceKind {
		return this.session?.device() ?? 'web';
	}

	/** Pending events (tests, debugging). */
	get pending(): ReadonlyArray<ActivityEvent> {
		return this.queue;
	}

	async flush(): Promise<void> {
		if (this.hidden || this.sending || this.queue.length === 0 || !this.session) return;
		this.clearTimer();
		if (!this.session.token()) {
			this.queue = [];
			this.persist();
			return;
		}
		if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
		const batch = this.queue.splice(0, BATCH);
		this.sending = true;
		try {
			const response = await http.post(Endpoints.KONVO_ACTIVITY_EVENTS, {body: {events: batch}, mode: 'silent'});
			// Keep them for later on rate limits and server errors; a 4xx means the batch itself is wrong.
			if (response.status === 429 || response.status >= 500 || response.status === 0) this.requeue(batch);
		} catch (error) {
			logger.debug('Could not send activity; keeping it for later', error);
			this.requeue(batch);
		} finally {
			this.sending = false;
			this.persist();
			this.schedule();
		}
	}

	private get hidden(): boolean {
		const visible = this.session?.visible?.();
		return (
			this.pageHidden ||
			(visible === undefined ? typeof document !== 'undefined' && document.visibilityState === 'hidden' : !visible)
		);
	}

	private clearTimer(): void {
		if (this.timer !== null) clearTimeout(this.timer);
		this.timer = null;
	}

	private schedule(): void {
		if (!this.listening || this.hidden || this.sending || this.queue.length === 0 || this.timer !== null) return;
		if (!this.session || !this.session.token() || navigator.onLine === false) return;
		this.timer = setTimeout(() => {
			this.timer = null;
			void this.flush();
		}, FLUSH_MS);
	}

	private requeue(batch: Array<ActivityEvent>): void {
		this.queue = [...batch, ...this.queue].slice(-MAX_QUEUE);
	}

	/** Sends what is left with a keepalive request (survives the page closing), or saves it. */
	private flushOnHide(): void {
		for (const callback of this.beforeFlush) {
			try {
				callback();
			} catch (error) {
				logger.debug('Activity before-flush hook failed', error);
			}
		}
		if (this.queue.length === 0) return;
		const token = this.session?.token();
		if (this.session && !token) {
			this.queue = [];
			this.persist();
			return;
		}
		const base = this.session?.apiBase();
		if (!token || !base || navigator.onLine === false) {
			this.persist();
			return;
		}
		const url = `${base}${Endpoints.KONVO_ACTIVITY_EVENTS}`;
		// Fire and forget: the page may be gone before the response, so a rejected promise does not
		// mean the server missed it (re-sending would count visits twice).
		while (this.queue.length > 0) {
			const batch = this.queue.splice(0, BATCH);
			try {
				void fetch(url, {
					method: 'POST',
					keepalive: true,
					headers: {'Content-Type': 'application/json', Authorization: token},
					body: JSON.stringify({events: batch}),
				}).catch(() => undefined);
			} catch {
				// keepalive quota exceeded: keep the rest for the next page load.
				this.requeue(batch);
				this.persist();
				return;
			}
		}
		this.persist();
	}

	private persist(): void {
		try {
			if (this.queue.length === 0) getProtectedLocalStorage()?.removeItem(STORAGE_KEY);
			else getProtectedLocalStorage()?.setItem(STORAGE_KEY, JSON.stringify(this.queue.slice(-MAX_QUEUE)));
		} catch {
			// Storage full or unavailable: the events are lost, which is fine for analytics.
		}
	}

	private restore(): void {
		try {
			const saved = getProtectedLocalStorage()?.getItem(STORAGE_KEY);
			if (!saved) return;
			getProtectedLocalStorage()?.removeItem(STORAGE_KEY);
			const events = JSON.parse(saved) as Array<ActivityEvent>;
			if (Array.isArray(events)) this.queue = [...events, ...this.queue].slice(-MAX_QUEUE);
		} catch {
			try {
				getProtectedLocalStorage()?.removeItem(STORAGE_KEY);
			} catch {
				// Storage unavailable: nothing to clear.
			}
		}
	}

	private readonly onVisibility = (): void => {
		if (document.visibilityState === 'hidden') this.onHide();
		else this.onShow();
	};

	private readonly onHide = (): void => {
		this.pageHidden = true;
		this.clearTimer();
		this.flushOnHide();
	};

	private readonly onShow = (): void => {
		this.pageHidden = false;
		void this.flush();
	};

	private readonly onOnline = (): void => void this.flush();
	private readonly onOffline = (): void => this.clearTimer();
}

const Activity = new ActivityStore();

/** The device this app runs on (phone, desktop app or web), as reported in events. */
export function activityDevice(): DeviceKind {
	return Activity.device;
}

export default Activity;
